import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useCompositeCbctTaskProcessing } from '~/backend/connector-api/connector-components';
import { GenerationModelDetails } from '~/domain/dicom/dicomBase';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useEnsureImageCached from '../useEnsureImageCached';

export type CompositeCbctImageRef = {
    seriesInstanceUID: string;
    studyInstanceUID: string;
};

export type CompositeCbctConnectorTaskInput = {
    cbct: CompositeCbctImageRef;
    baseCt: CompositeCbctImageRef;
};

// The connector hard-codes the base model + customization server-side,
// so we don't pass them in the request body. We still annotate the
// result files with the same base-model/customization identity for
// provenance when `processCreatedFiles` tags the generated DICOM.
const COMPOSITE_CBCT_BASE_MODEL = 'reg_ar_cc_al';
const COMPOSITE_CBCT_CUSTOMIZATION = 'composite_cbct';

const COMPOSITE_CBCT_GENERATION_MODEL: GenerationModelDetails = {
    name: COMPOSITE_CBCT_BASE_MODEL,
    label: 'Composite CBCT',
    customizationName: COMPOSITE_CBCT_CUSTOMIZATION,
};

/**
 * Connector-mode submission of a composite CBCT task. Both input
 * images are pushed into the connector cache (if not already there)
 * and the dedicated `/api/tasks/composite-cbct` endpoint is POSTed;
 * on completion the composite is loaded as the viewport's primary
 * image, mirroring the backend-mode handler.
 *
 * Differs from `useContourPropagationTask` in that it has no
 * structure-set input (the connector's composite-CBCT endpoint
 * accepts only the two image series) and the result load path
 * swaps the primary image rather than setting a structure set.
 */
export default function useCompositeCbctConnectorTask(onSuccess?: () => void) {
    const sessionData = useRecoilValue(sessionDataAtom);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patient = useSelectedPatient();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startCompositeCbct } =
        useCompositeCbctTaskProcessing();
    const { ensureCached } = useEnsureImageCached();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentPrimaryImage } = useViewportEnvironment();

    const buildOnLoad = (patientId: string) => async (job: Job) => {
        if (!job.results || job.results.length === 0) {
            return;
        }
        const processedItems = await processCreatedFiles(
            job.results,
            COMPOSITE_CBCT_GENERATION_MODEL
        );
        if (!processedItems) {
            return;
        }
        updateJob(job.id, { isImported: true });
        // Load the composite as the new primary image. The backend
        // does not emit a registration bridging CBCT ↔ composite, so
        // we cannot meaningfully attach the CBCT input as secondary
        // for blending — the user can pick one manually via
        // ImageHeader's `+ Blend images` if they want to compare.
        const generatedImage = processedItems.images[0];
        if (generatedImage) {
            setEnvironmentPrimaryImage([generatedImage], patientId);
        }
    };

    const startProcessing = async (input: CompositeCbctConnectorTaskInput) => {
        if (isSubmitting) {
            return;
        }
        const { cbct, baseCt } = input;

        // The connector and the local viewport can refer to the same
        // patient by different identifiers: the connector task API
        // wants the session's patient id (which is the id the
        // connector indexed its cache under), while the viewport
        // setters look up the patient in the local repository via
        // `selectedPatientIdState` / `patient?.dataSet.PatientID`.
        // Threading both keeps each side consistent — using the
        // connector id for viewport updates would silently no-op the
        // primary setter while the secondary still runs. Same split
        // used by `useDosePredictionTask`.
        const connectorPatientId =
            sessionData?.payload?.patient?.id ??
            selectedPatientId ??
            patient?.dataSet.PatientID;
        const viewportPatientId =
            selectedPatientId ?? patient?.dataSet.PatientID ?? '';

        if (!connectorPatientId) {
            handleError(
                new Error('No patient context available for Composite CBCT.')
            );
            return;
        }
        if (!viewportPatientId) {
            handleError(
                new Error(
                    'No local patient context — cannot update the viewport.'
                )
            );
            return;
        }
        if (!cbct?.seriesInstanceUID || !baseCt?.seriesInstanceUID) {
            handleError(
                new Error('Both CBCT and base CT images must be selected.')
            );
            return;
        }
        if (cbct.seriesInstanceUID === baseCt.seriesInstanceUID) {
            handleError(
                new Error('CBCT and base CT must be different image series.')
            );
            return;
        }

        setIsSubmitting(true);
        try {
            // Check / upload each input on the connector. When the
            // series is already cached — the common case in session
            // flows, since the Eclipse plugin launches the UI with
            // the connector already holding every image listed in
            // the session — `ensureCached` short-circuits via
            // `findImageIdOnConnector` and no local `Image3D` is
            // needed. Only the fallback (connector does not have the
            // series) requires a loaded Image3D; callers should have
            // materialised the image locally before reaching this
            // path in that case.
            await ensureCached({
                patientId: connectorPatientId,
                seriesInstanceUID: cbct.seriesInstanceUID,
                studyInstanceUID: cbct.studyInstanceUID,
            });
            await ensureCached({
                patientId: connectorPatientId,
                seriesInstanceUID: baseCt.seriesInstanceUID,
                studyInstanceUID: baseCt.studyInstanceUID,
            });

            const task = await startCompositeCbct({
                body: {
                    cbctImageSeries: {
                        patientID: connectorPatientId,
                        seriesInstanceUID: cbct.seriesInstanceUID,
                        studyInstanceUID: cbct.studyInstanceUID,
                    },
                    baseCtImageSeries: {
                        patientID: connectorPatientId,
                        seriesInstanceUID: baseCt.seriesInstanceUID,
                        studyInstanceUID: baseCt.studyInstanceUID,
                    },
                },
            });

            const newJob: NewJobData = {
                name: 'Composite CBCT',
                id: task.id,
                type: WorkflowTypes.IMAGE,
                onLoad: buildOnLoad(viewportPatientId),
                task: {
                    model: {
                        name: COMPOSITE_CBCT_BASE_MODEL,
                        bodyPart: '',
                        label: 'Composite CBCT',
                        type: 'registration',
                        tags: [],
                        inputModalities: ['CT'],
                        isFreeTextEntryModel: false,
                        version: '',
                        isLatest: true,
                    },
                    output: task,
                },
            };

            addJob(newJob);
            onSuccess?.();
        } catch (error) {
            handleError(error, 'Failed to start Composite CBCT task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startProcessing, isSubmitting };
}
